import PropTypes from 'prop-types'
import { Fragment } from 'react'
import { esCobroPorPublicar } from '../../constants/publicaciones.js'
import { AUMENTO_TIENDA_OFICIAL } from '../../constants/tiendaOficial.js'
import { toNumber } from '../helpers/calcularPrecioTotal'
import { formatearNumeroAR } from '../helpers/formatearNumeroAR'
import Precios from '../reutilizables/precios/Precios'
import styles from './DetalleCobro.module.css'

const DetalleCobro = ({
  cantidad,
  precio,
  porcentajeExposicion,
  exposicion,
  monedaSimbolo,
  alicuota,
  precioEnvio,
  esTiendaOficial,
  porcComision,
  porcComisionTienda,
  montoComision,
  tipoCobro,
  descuentoExposicion,
  descuentoTiendaOficial,
  totalCobro,
  percepciones = []
}) => {
  const precioUnitario = Number(precio) || 0
  const cantidadNumerica = Number(cantidad) || 1
  const alicuotaNumerica = Number(alicuota) || 0

  const comisionCongelada = montoComision !== null && montoComision !== undefined
  const esAvisoPagadoAlPublicar = esCobroPorPublicar({ tipoCobro })
  const porcExposicionAplicado = comisionCongelada
    ? Number(porcComision) || 0
    : Number(porcentajeExposicion) || 0
  const porcTiendaAplicado = comisionCongelada
    ? Number(porcComisionTienda) || 0
    : esTiendaOficial
      ? AUMENTO_TIENDA_OFICIAL
      : 0

  const descuentoExposicionAplicado = toNumber(descuentoExposicion)
  const descuentoTiendaOficialAplicado = toNumber(descuentoTiendaOficial)
  const totalCalculado = toNumber(totalCobro)
  const descuentoExposicionFormateado = formatearNumeroAR(descuentoExposicionAplicado)
  const descuentoTiendaOficialFormateado = formatearNumeroAR(descuentoTiendaOficialAplicado)

  const percepcionesAplicadas = (percepciones ?? []).filter((p) => Number(p.monto) > 0)

  return (
    <div className={styles.contenedor}>
      <h3 className={styles.titulo}>Detalle de Cobro</h3>
      <div className={styles.tabla}>
        <div className={`${styles.celdaDescripcion} ${styles.encabezado}`}>Descripción</div>
        <div className={`${styles.celdaImporte} ${styles.encabezado}`}>Importe</div>

        <div className={styles.celdaDescripcion}>Venta (x {cantidad})</div>
        <div className={styles.celdaImporte}>
          <Precios
            precio={precioUnitario}
            monedaSimbolo={monedaSimbolo}
            alicuota={alicuotaNumerica}
            cantidad={cantidadNumerica}
            showSinIVA={true}
          />
        </div>

        {precioEnvio > 0 && (
          <>
            <div className={styles.celdaDescripcion}>Envío</div>
            <div className={styles.celdaImporte}>
              <Precios
                precio={precioEnvio}
                monedaSimbolo={monedaSimbolo}
                alicuota={21}
                showSinIVA={true}
              />
            </div>
          </>
        )}

        {esAvisoPagadoAlPublicar ? (
          <>
            <div className={styles.celdaDescripcion}>Exposición {exposicion}</div>
            <div className={styles.celdaImporte}>
              <small>Aviso abonado al publicar</small>
            </div>
          </>
        ) : (
          <>
            <div className={styles.celdaDescripcion}>
              Exposición {exposicion} ({porcExposicionAplicado}%)
            </div>
            <div className={`${styles.celdaImporte} ${styles.descuento}`}>
              <b>
                -{monedaSimbolo}
                {descuentoExposicionFormateado}
              </b>
            </div>
          </>
        )}

        {porcTiendaAplicado > 0 && (
          <>
            <div className={styles.celdaDescripcion}>Tienda Oficial ({porcTiendaAplicado}%)</div>
            <div className={`${styles.celdaImporte} ${styles.descuento}`}>
              <b>
                -{monedaSimbolo}
                {descuentoTiendaOficialFormateado}
              </b>
            </div>
          </>
        )}

        {percepcionesAplicadas.map((percepcion) => (
          <Fragment key={percepcion.idProvincia}>
            <div className={`${styles.celdaDescripcion} ${styles.celdaPercepcion}`}>
              IIBB {percepcion.nombreProvincia} ({percepcion.alicuota}%)
              <small className={styles.aclaracion}>Lo abona el comprador</small>
            </div>
            <div className={`${styles.celdaImporte} ${styles.celdaPercepcion}`}>
              {monedaSimbolo}
              {formatearNumeroAR(percepcion.monto)}
            </div>
          </Fragment>
        ))}

        <div className={`${styles.celdaDescripcion} ${styles.filaTotal}`}>Total</div>
        <div className={`${styles.celdaImporte} ${styles.filaTotal}`}>
          <Precios
            precio={totalCalculado}
            monedaSimbolo={monedaSimbolo}
            alicuota={0}
            showSinIVA={false}
          />
        </div>
      </div>
    </div>
  )
}

DetalleCobro.propTypes = {
  precio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  cantidad: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  monedaSimbolo: PropTypes.string.isRequired,
  exposicion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  porcentajeExposicion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  alicuota: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  precioEnvio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  esTiendaOficial: PropTypes.bool.isRequired,
  porcComision: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  porcComisionTienda: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  montoComision: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tipoCobro: PropTypes.string,
  descuentoExposicion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  descuentoTiendaOficial: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  totalCobro: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  percepciones: PropTypes.arrayOf(
    PropTypes.shape({
      idProvincia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      nombreProvincia: PropTypes.string,
      alicuota: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      monto: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  )
}

export default DetalleCobro
